import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { currentWeek } from "@/lib/week";
import { LoadError, errMessage } from "@/components/LoadError";
import { Tegel, SectieKop, LIJST, KAART, KNOP_IN_KOP } from "@/components/rust/Rust";
import { AfdelingAfspraakRegel } from "@/components/afdelingen/AfdelingAfspraakRegel";
import { cap } from "@/components/afspraken/afsprakenUtils";

const tijd = (iso) => new Date(iso).toLocaleString("nl-NL", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

// v19-C2: Vandaag van een afdelingsgebruiker.
export default function AfdelingVandaag() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [fout, setFout] = useState("");
  const laad = useCallback(() => api.get("/vandaag").then((r) => { setD(r.data); setFout(""); }).catch((e) => setFout(errMessage(e))), []);
  useEffect(() => { laad(); }, [laad]);
  const gezien = () => api.post("/vandaag/gezien", { generatedAt: d.generatedAt }).then(() => setD((p) => ({ ...p, nieuw: [], tellers: { ...p.tellers, nieuw: 0 } }))).catch(() => toast.error("Opslaan mislukt"));
  const vink = async (t) => {
    setD((p) => ({ ...p, weekritme: { ...p.weekritme, taken: p.weekritme.taken.map((x) => (x.id === t.id ? { ...x, done: !t.done } : x)) } }));
    try { await api.post("/weekritme/toggle", { week: currentWeek(), om: user.username, taskId: t.id, done: !t.done }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); laad(); }
  };
  if (fout) return <LoadError error={fout} onRetry={laad} testid="afd-vandaag-error" />;
  if (!d) return <div data-testid="afd-vandaag-laden" className="p-12 text-center text-sm text-zinc-400">Laden…</div>;
  const wr = d.weekritme;
  return (
    <div data-testid="afd-vandaag-page" className="pb-24 md:pb-0 max-w-3xl">
      <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-900">Vandaag</h1>
      <p data-testid="afd-vandaag-sub" className="text-sm text-zinc-500 mb-3 md:mb-4">{d.datumLabel}</p>
      <div className="grid grid-cols-3 gap-2 md:gap-3 mb-3 md:mb-4" data-testid="afd-vandaag-tellers">
        <Tegel testid="afd-teller-te-laat" label="Te laat" value={d.tellers.teLaat} rood />
        <Tegel testid="afd-teller-deze-week" label="Deze week" value={d.tellers.dezeWeek} />
        <Tegel testid="afd-teller-nieuw" label="Nieuw" value={d.tellers.nieuw} />
      </div>
      <div className="mb-3 md:mb-4">
        <SectieKop titel="Mijn afspraken" aantal={d.afspraken.length} />
        {d.afspraken.length ? (
          <ul className={LIJST} data-testid="afd-vandaag-afspraken">
            {d.afspraken.map((a) => <li key={a.id}><AfdelingAfspraakRegel a={a} testid={`afd-vandaag-afspraak-${a.id}`} /></li>)}
          </ul>
        ) : <div className={`${KAART} px-3 py-4 text-sm text-zinc-400`}>Geen open afspraken.</div>}
      </div>
      <div className="mb-3 md:mb-4">
        <SectieKop titel={`Weekritme · ${wr.dag.toLowerCase()}`} aantal={`${wr.taken.filter((t) => t.done).length}/${wr.taken.length}`} actie={wr.concept ? <span data-testid="afd-vandaag-concept" className="text-[11px] font-semibold text-amber-300">concept</span> : null} />
        {wr.taken.length ? (
          <ul className={LIJST} data-testid="afd-vandaag-weekritme">
            {wr.taken.map((t) => (
              <li key={t.id}>
                <button type="button" data-testid={`afd-vandaag-taak-${t.id}`} onClick={() => vink(t)} className="w-full text-left flex items-start gap-3 px-3 py-2.5 min-h-[44px] hover:bg-zinc-50">
                  <span className={`mt-0.5 h-5 w-5 shrink-0 rounded-sm border flex items-center justify-center ${t.done ? "bg-emerald-500 border-emerald-500" : "border-zinc-300 bg-white"}`}>{t.done && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}</span>
                  <span className={`text-[13.5px] ${t.done ? "text-zinc-400 line-through" : "text-zinc-800"}`}>{t.text}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : <div className={`${KAART} px-3 py-4 text-sm text-zinc-400`}>Vandaag geen taken. <Link to="/weekritme" className="underline">Hele week</Link></div>}
      </div>
      {d.overleggen.length > 0 && (
        <div className="mb-3 md:mb-4">
          <SectieKop titel="Volgend overleg" aantal={d.overleggen.length} />
          <ul className={LIJST} data-testid="afd-vandaag-overleg">
            {d.overleggen.map((o) => <li key={o.afdeling} className="px-3 py-2.5 text-[13.5px] text-zinc-800">{o.naam}: <b className="font-semibold">{o.datumLabel}</b> met {o.met}</li>)}
          </ul>
        </div>
      )}
      <div className="mb-3 md:mb-4">
        <SectieKop titel="Nieuw sinds je vorige bezoek" aantal={d.nieuw.length} actie={d.nieuw.length ? <button type="button" data-testid="afd-vandaag-gezien" onClick={gezien} className={KNOP_IN_KOP}>Gezien</button> : null} />
        {d.nieuw.length ? (
          <ul className={LIJST} data-testid="afd-vandaag-nieuw">
            {d.nieuw.map((n, i) => (
              <li key={`${n.afspraakId}-${n.soort}-${i}`}>
                <Link to={`/afspraken?select=${n.afspraakId}`} data-testid={`afd-vandaag-nieuw-${i}`} className="px-3 py-[9px] text-[13px] flex flex-wrap gap-x-3 items-baseline hover:bg-zinc-50">
                  <span className="flex-1 min-w-[160px] text-zinc-700">{n.soort === "nieuw" ? `Nieuwe afspraak van ${cap(n.door)}` : n.status === "nagekomen" ? "Afgerond: nagekomen" : "Afgerond: niet nagekomen"} · {n.wat}</span>
                  <span className="font-mono text-xs text-zinc-400">{n.at ? tijd(n.at) : ""}</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : <div className={`${KAART} px-3 py-4 text-sm text-zinc-400`}>Niets nieuws.</div>}
      </div>
    </div>
  );
}
